// src/components/category_page/ProductGrid.js
import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { useProductData, productImageUrl, productImageAlt, productInStock } from "../../context/ProductDataContext";
import { filterProducts } from "../../lib/productAttributes";
import { useCart } from "../CartContext";
import { HiOutlineShoppingCart } from "react-icons/hi";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05 } }),
};

const fmtFt = (n) =>
  typeof n === "number" ? `${n.toLocaleString("hu-HU")} Ft` : "—";

export default function ProductGrid({ activeFilters = {}, onAddToCart }) {
  const [qtyById, setQtyById] = useState({});
  const { addToCart: addToCartCtx, openCart } = useCart();
  const { products, loading, attributeConfig } = useProductData();

  const safeProducts = useMemo(() => (Array.isArray(products) ? products : []), [products]);

  const filtered = useMemo(() => {
    let list = filterProducts(safeProducts, activeFilters, attributeConfig);
    const sort = activeFilters?.sort || "relevance";
    if (sort === "price-asc") list = list.slice().sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === "price-desc") list = list.slice().sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sort === "name-asc") list = list.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"));
    else if (sort === "name-desc") list = list.slice().sort((a, b) => (b.name || "").localeCompare(a.name || "", "hu"));
    return list;
  }, [safeProducts, activeFilters, attributeConfig]);

  const setQty = useCallback(
    (id, q) =>
      setQtyById((prev) => ({
        ...prev,
        [id]: Math.max(0, Math.min(99, Number(q) || 0)),
      })),
    []
  );

  const inc = useCallback((id) => setQty(id, (qtyById[id] || 0) + 1), [qtyById, setQty]);
  const dec = useCallback((id) => setQty(id, (qtyById[id] || 0) - 1), [qtyById, setQty]);

  if (loading) return <div className="p-4 text-center text-gray-600">Betöltés…</div>;

  if (!filtered.length) {
    return <div className="p-4 text-center text-gray-600">Nincs találat a megadott szűrőkkel.</div>;
  }

  return (
    <motion.div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
      initial="hidden"
      animate="visible"
    >
      {filtered.map((prod, i) => {
        const qty = qtyById[prod.id] || 0;
        const active = qty > 0;
        const imgSrc = productImageUrl(prod);
        const priceText = fmtFt(prod.price);

        const handleAdd = (e) => {
          e.preventDefault();
          e.stopPropagation();

          const q = Math.max(1, Number.isFinite(qty) ? qty : 1);

          if (typeof onAddToCart === "function") {
            onAddToCart(prod, q);
          } else if (typeof addToCartCtx === "function") {
            addToCartCtx(prod, q);
            if (typeof openCart === "function") openCart();
          }
        };

        return (
          <motion.div key={prod.id || prod.slug} custom={i} variants={itemVariants}>
            <Link to={`/termek/${prod.id || prod.slug}`} aria-label={`Megnyitás: ${prod.name}`}>
              <div
                className={`bg-white shadow rounded-xl p-3 sm:p-4 hover:shadow-lg transition border ${
                  active ? "border-blue-500 ring-1 ring-blue-200" : "border-transparent"
                }`}
              >
                <div className="w-full aspect-square overflow-hidden rounded-lg mb-2 bg-gray-50">
                  <img
                    src={imgSrc}
                    alt={productImageAlt(prod)}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = "/img/placeholder.webp";
                    }}
                  />
                </div>

                <h3 className="text-[13px] sm:text-sm md:text-base font-semibold leading-snug line-clamp-2 min-h-[2.4em]">
                  {prod.name}
                </h3>

                {prod.subtitle && (
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                    {prod.subtitle}
                  </p>
                )}

                <div className="mt-2 flex items-center justify-between">
                  <div className="text-sm sm:text-base font-medium">{priceText}</div>
                  <div className={`text-[11px] sm:text-xs ${productInStock(prod) ? "text-emerald-600" : "text-amber-600"}`}>
                    {productInStock(prod) ? "Raktáron" : "Rendelésre"}
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    className="h-8 w-8 sm:h-9 sm:w-9 border rounded hover:bg-gray-50 flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dec(prod.id);
                    }}
                    aria-label="Egy darabbal kevesebb"
                  >
                    −
                  </button>

                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={Number.isFinite(qty) ? qty : 0}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setQty(prod.id, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-8 sm:h-9 w-12 xs:w-12 sm:w-14 md:w-16 border rounded text-center text-sm sm:text-base min-w-0 flex-shrink"
                    aria-label="Mennyiség"
                  />

                  <button
                    type="button"
                    className="h-8 w-8 sm:h-9 sm:w-9 border rounded hover:bg-gray-50 flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      inc(prod.id);
                    }}
                    aria-label="Egy darabbal több"
                  >
                    +
                  </button>

                  <button
                    type="button"
                    className={`ml-auto h-8 sm:h-9 aspect-square rounded-lg flex items-center justify-center ${
                      qty > 0 ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    onClick={handleAdd}
                    aria-label="Kosárba"
                    title="Kosárba"
                  >
                    <HiOutlineShoppingCart className="text-lg sm:text-xl" />
                  </button>
                </div>
              </div>
            </Link>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

ProductGrid.propTypes = {
  activeFilters: PropTypes.object,
  onAddToCart: PropTypes.func,
};
